import {
  ArrowCounterClockwiseIcon,
  NotePencilIcon,
  TrashIcon,
  XCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import type { TaskPriority } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { T, useGT, Var } from 'gt-react';
import { PRIORITY_DOT_COLORS } from './constants';
import { cn } from 'src/lib/utils';

const PRIORITY_OPTIONS: TaskPriority[] = ['urgent', 'high', 'medium', 'low'];

interface BulkActionsBarProps {
  selectedCount: number;
  onClear: () => void;
  onReset: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onSetPriority: (priority: TaskPriority) => void;
  onEditDescription: () => void;
  isResetting: boolean;
  isCancelling: boolean;
  isDeleting: boolean;
  isUpdatingPriority: boolean;
  isEditingDescription: boolean;
}

export function BulkActionsBar({
  selectedCount,
  onClear,
  onReset,
  onCancel,
  onDelete,
  onSetPriority,
  onEditDescription,
  isResetting,
  isCancelling,
  isDeleting,
  isUpdatingPriority,
  isEditingDescription,
}: BulkActionsBarProps) {
  const gt = useGT();
  const isBusy =
    isResetting ||
    isCancelling ||
    isDeleting ||
    isUpdatingPriority ||
    isEditingDescription;

  return (
    <div className="sticky top-0 z-10 mb-2 rounded-md border bg-accent px-3 py-2 text-sm shadow-sm space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="font-medium text-xs">
          <T>
            <Var>{selectedCount}</Var> selected
          </T>
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onClear}
          aria-label={gt('Clear selection')}
        >
          <XIcon className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs px-2"
          onClick={onEditDescription}
          disabled={isBusy}
        >
          <NotePencilIcon className="mr-1 h-3.5 w-3.5" />
          <T>Edit description</T>
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs px-2"
              disabled={isBusy}
            >
              <T>Priority</T>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {PRIORITY_OPTIONS.map((priority) => (
              <DropdownMenuItem
                key={priority}
                onClick={() => onSetPriority(priority)}
              >
                <span
                  className={cn(
                    'mr-2 inline-block h-2 w-2 rounded-full shrink-0',
                    PRIORITY_DOT_COLORS[priority],
                  )}
                />
                <span className="capitalize">{priority}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs px-2"
          onClick={onReset}
          disabled={isBusy}
        >
          <ArrowCounterClockwiseIcon className="mr-1 h-3.5 w-3.5" />
          <T>Reset</T>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs px-2"
          onClick={onCancel}
          disabled={isBusy}
        >
          <XCircleIcon className="mr-1 h-3.5 w-3.5" />
          <T context="bulk action: cancel the selected tasks">Cancel</T>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs px-2 text-destructive hover:text-destructive"
          onClick={onDelete}
          disabled={isBusy}
        >
          <TrashIcon className="mr-1 h-3.5 w-3.5" />
          <T>Delete</T>
        </Button>
      </div>
    </div>
  );
}
